import { Coding, Encounter } from "@medplum/fhirtypes";

import { format } from "date-fns";

import { formatTime, getLocalDateAndTimeInTimezone } from "@/utils/datetime";

import { getEncounterServiceDateDetailsFromEncounter } from "./encounter-service-date-form";

export interface EncounterServiceDateDiffDisplay {
  date: string;
  time: string;
  timezone: string;
}

export interface EncounterServiceDateDiffContext {
  current: EncounterServiceDateDiffDisplay;
  previous: EncounterServiceDateDiffDisplay;
  hasChanges: boolean;
}

export type EncounterServiceDateAnswersFormat = "array" | "single";

interface QuestionnaireAnswerItem {
  valueDateTime?: string;
  valueCoding?: Coding;
}

interface CanonicalEncounterServiceDate {
  dateKey: string;
  timeKey: string;
  timezoneKey: string;
  display: EncounterServiceDateDiffDisplay;
}

/**
 * Builds display and comparison values for encounter service date diff fields.
 *
 * Each side reads only its own answers, so a version that recorded no service date yields no diff.
 *
 * @param params Current/previous questionnaire answers and answer shape.
 * @returns Diff context when date, time, or timezone changed; otherwise null.
 */
export function getEncounterServiceDateDiffContext(params: {
  currentAnswers: Record<string, unknown>;
  previousAnswers: Record<string, unknown>;
  answersFormat?: EncounterServiceDateAnswersFormat;
}): EncounterServiceDateDiffContext | null {
  const { currentAnswers, previousAnswers, answersFormat = "array" } = params;

  const currentCanonical = getCanonicalEncounterServiceDate(currentAnswers, answersFormat);
  const previousCanonical = getCanonicalEncounterServiceDate(previousAnswers, answersFormat);

  if (!currentCanonical || !previousCanonical) {
    return null;
  }

  const hasChanges =
    currentCanonical.dateKey !== previousCanonical.dateKey ||
    currentCanonical.timeKey !== previousCanonical.timeKey ||
    currentCanonical.timezoneKey !== previousCanonical.timezoneKey;

  if (!hasChanges) {
    return null;
  }

  return {
    current: currentCanonical.display,
    previous: previousCanonical.display,
    hasChanges,
  };
}

/**
 * Builds the service-date diff from two Encounter versions (current vs previous).
 *
 * Companion to {@link getEncounterServiceDateDiffContext} for the encounter-history diff path,
 * where both sides come from the Encounter resource rather than questionnaire answers.
 *
 * @returns Diff context when date, time, or timezone changed; otherwise null.
 */
export function getEncounterServiceDateDiffContextFromEncounters(
  currentEncounter: Encounter | undefined,
  previousEncounter: Encounter | undefined,
): EncounterServiceDateDiffContext | null {
  const currentCanonical = getCanonicalEncounterServiceDateFromEncounter(currentEncounter);
  const previousCanonical = getCanonicalEncounterServiceDateFromEncounter(previousEncounter);

  if (!currentCanonical || !previousCanonical) {
    return null;
  }

  const hasChanges =
    currentCanonical.dateKey !== previousCanonical.dateKey ||
    currentCanonical.timeKey !== previousCanonical.timeKey ||
    currentCanonical.timezoneKey !== previousCanonical.timezoneKey;

  if (!hasChanges) {
    return null;
  }

  return {
    current: currentCanonical.display,
    previous: previousCanonical.display,
    hasChanges,
  };
}

function getAnswerItem(
  answers: Record<string, unknown>,
  linkId: string,
  answersFormat: EncounterServiceDateAnswersFormat,
): QuestionnaireAnswerItem | undefined {
  const entry = answers[linkId];
  if (!entry) {
    return undefined;
  }

  if (answersFormat === "array") {
    const items = entry as QuestionnaireAnswerItem[];
    return items[0];
  }

  return entry as QuestionnaireAnswerItem;
}

function getCanonicalEncounterServiceDate(
  answers: Record<string, unknown>,
  answersFormat: EncounterServiceDateAnswersFormat,
): CanonicalEncounterServiceDate | undefined {
  const periodStart = getAnswerItem(answers, "encounterStartDate", answersFormat)?.valueDateTime;
  const timezoneCoding = getAnswerItem(
    answers,
    "encounterStartDateTimezone",
    answersFormat,
  )?.valueCoding;

  return buildCanonicalEncounterServiceDate(periodStart, timezoneCoding);
}

function getCanonicalEncounterServiceDateFromEncounter(
  encounter: Encounter | undefined,
): CanonicalEncounterServiceDate | undefined {
  if (!encounter?.period?.start) {
    return undefined;
  }

  const serviceDateDetails = getEncounterServiceDateDetailsFromEncounter(encounter);

  return buildCanonicalEncounterServiceDate(
    encounter.period.start,
    serviceDateDetails.encounterStartDateTimezone,
  );
}

function buildCanonicalEncounterServiceDate(
  periodStart: string | undefined,
  timezoneCoding: Coding | undefined,
): CanonicalEncounterServiceDate | undefined {
  if (!periodStart || !timezoneCoding?.code) {
    return undefined;
  }

  const { date, time } = getLocalDateAndTimeInTimezone(periodStart, timezoneCoding.code);

  return {
    dateKey: format(date, "yyyy-MM-dd"),
    timeKey: time,
    timezoneKey: timezoneCoding.code,
    display: {
      date: format(date, "MM/dd/yyyy"),
      time: formatTime(time),
      timezone: timezoneCoding.display ?? timezoneCoding.code,
    },
  };
}
